import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ArcButton, ArcStack } from '@src/core/ui';
import { routesConfig } from '@src/core/routes';
import { MissionIdParams } from '@src/core/entities';
import { useCancelMissionMutation, useTakeOffMutation, useGetOrderMissionQuery } from '@src/features/order/redux';
import { toast } from 'react-toastify';
import { ChecklistActionsProps } from './types';
import { trackError, toTrackedError } from '@src/core/sentry';
import { addOptimisticMissionSerial } from '@src/features/drone/redux/actions';

const ChecklistActions: React.FC<ChecklistActionsProps> = ({ allChecked }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { missionId } = useParams<MissionIdParams>();
  const { data } = useGetOrderMissionQuery(missionId);
  const [cancelMission, { isLoading: isCanceling }] = useCancelMissionMutation();
  const [takeOff, { isLoading: isTakingOff }] = useTakeOffMutation();

  const handleCancel = () => {
    if (!missionId) return;
    cancelMission(missionId).unwrap().then(() => {
      toast.success('Mission has been canceled');
      navigate(routesConfig.orders, { replace: true });
    }).catch((err: unknown) => {
      trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'checklistCancelMission' } });
    });
  };

  const handleExit = () => {
    navigate(routesConfig.orders);
  };

  const handleTakeOff = () => {
    if (!missionId || !allChecked) return;
    takeOff({ missionId }).unwrap().then(() => {
      const firstOrderId = data?.orders?.[0]?.id;
      const droneSerial = data?.drone?.serialNumber;
      const params = new URLSearchParams();
      if (droneSerial) params.set('droneSerial', droneSerial);
      params.set('missionId', missionId);
      if (firstOrderId != null) params.set('orderId', String(firstOrderId));
      // Bridge the gap before backend flips drone status to MISSION (UPDATE_DRONES
      // WS push can lag 10-15s); cleared by DronesSidebar once real status arrives.
      if (droneSerial) {
        dispatch(addOptimisticMissionSerial(droneSerial));
      }
      navigate(`${routesConfig.drones}?${params.toString()}`);
      toast.success('Take Off Success Text');
    }).catch((err: unknown) => {
      trackError(toTrackedError(err), { code: 'FE_0020', extra: { flow: 'checklistTakeOff' } });
    });
  };

  return (
    <ArcStack spacing={1} width='100%'>
      <ArcStack direction='row' spacing={1}>
        <ArcButton.ghost 
          fullWidth 
          isLoading={isCanceling} 
          onClick={handleCancel}
        > 
          CANCEL 
        </ArcButton.ghost>
        <ArcButton.ghost 
          fullWidth 
          onClick={handleExit}
        > 
          EXIT 
        </ArcButton.ghost>
      </ArcStack>
      <ArcButton.brand 
        fullWidth
        disabled={!allChecked}
        isLoading={isTakingOff}
        onClick={handleTakeOff}
      > 
        TAKEOFF 
      </ArcButton.brand>
    </ArcStack>
  );
};

export default ChecklistActions;

